import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { grey } from "@mui/material/colors";

export default function ProductSelectDialog({
  open,
  onClose,
  products = [],
  disabledProductIds = [],
  onSelect,
}) {
  const theme = useTheme();
  const [catFilter, setCatFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const handleClose = () => {
    setSelectedIds([]);      
    // (ถ้าอยากรีเซ็ต filter/search ด้วยก็เปิดสองบรรทัดล่างนี้)
    // setCatFilter(null);
    // setSearchQuery("");
    onClose?.();
  };

  const colorPalette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];

  const categories = useMemo(() => {
    const seen = new Set();
    return products
      .map((p) => p.category)
      .filter((cat) => cat && !seen.has(cat.name) && seen.add(cat.name));
  }, [products]);

  const filteredProds = useMemo(() => {
    return products.filter((p) => {
      const okCat = !catFilter || p.category?.name === catFilter;
      const okSearch = p.title?.toLowerCase().includes(searchQuery.toLowerCase());
      const notDisabled = !disabledProductIds.includes(p.id);
      return okCat && okSearch && notDisabled;
    });
  }, [products, catFilter, searchQuery, disabledProductIds]);

  const catColorMap = useMemo(() => {
    return categories.reduce((m, c, i) => {
      m[c.name] = colorPalette[i % colorPalette.length];
      return m;
    }, {});
  }, [categories, colorPalette]);

  const toggleSelect = (id) =>
    setSelectedIds((ids) => (ids.includes(id) ? [] : [id]));

  const handleConfirm = () => {
    if (selectedIds.length === 1) {
      const prod = products.find((p) => p.id === selectedIds[0]);
      onSelect?.(prod);
      setSelectedIds([]); // เคลียร์หลังยืนยันด้วย
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>เลือกสินค้า</DialogTitle>
      <DialogContent sx={{ display: "flex", p: 2 }}>
        {/* Sidebar */}
        <Paper variant="outlined" sx={{ width: 240, maxHeight: "70vh", overflowY: "auto", mr: 2 }}>
          <Box
            p={2}
            sx={{ cursor: "pointer", bgcolor: catFilter === null ? "action.selected" : "transparent" }}
            onClick={() => setCatFilter(null)}
          >
            ทั้งหมด
          </Box>
          {categories.map((cat) => (
            <Box
              key={cat.name}
              p={2}
              sx={{ cursor: "pointer", bgcolor: catFilter === cat.name ? "action.selected" : "transparent" }}
              onClick={() => setCatFilter(cat.name)}
            >
              {cat.name}
            </Box>
          ))}
        </Paper>

        {/* Content */}
        <Box flex={1} display="flex" flexDirection="column">
          <TextField
            fullWidth
            placeholder="ค้นหาชื่อสินค้า..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box flex={1} overflow="auto">
            <Grid container spacing={2}>
              {filteredProds.map((p) => {
                const isSel = selectedIds.includes(p.id);
                return (
                  <Grid item xs={6} sm={4} md={3} key={p.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        bgcolor: isSel
                          ? theme.palette.grey[300]
                          : alpha(catColorMap[p.category.name] || grey[200], 0.2),
                        borderColor: isSel ? "success.main" : undefined,
                      }}
                    >
                      <CardActionArea onClick={() => toggleSelect(p.id)}>
                        <CardContent>
                          <Typography noWrap>{p.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.category.name}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Chip label={`เลือกแล้ว ${selectedIds.length} ชิ้น`} color={selectedIds.length === 1 ? "success" : "default"} />
        <Button size="large" onClick={handleConfirm} disabled={selectedIds.length !== 1}>
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
}
